import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function CurrentBooking() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ darshan_types: [] });
  const [form, setForm] = useState({
    devotee_name: "", aadhaar: "", mobile: "", reference_name: "",
    num_devotees: 1, darshan_type_id: "", paid_free: "", payment_details: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/meta/form-data").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedDt = meta.darshan_types.find((d) => d.id === form.darshan_type_id);
  const isFreeType = selectedDt?.paid_free === "FREE";
  const totalN = Math.max(1, Number(form.num_devotees) || 1);
  const paidN = isFreeType ? 0 : Math.min(Math.max(0, Number(form.paid_devotees ?? totalN)), totalN);
  const freeN = totalN - paidN;
  const totalAmount = (selectedDt?.amount || 0) * paidN;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/protocol/current", { ...form, num_devotees: totalN, paid_devotees: paidN });
      setResult(r.data);
      toast.success("Booking registered");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-xl mx-auto" data-testid="current-success">
        <Card className="card-gold-top shadow-lg text-center">
          <CardContent className="p-8 space-y-4">
            <CheckCircle2 className="w-14 h-14 text-green-700 mx-auto" />
            <h2 className="font-heading text-2xl font-bold text-maroon-900">Booking Registered</h2>
            <p className="font-mono text-xl font-bold text-maroon-900" data-testid="current-success-txn">{result.transaction_id}</p>
            <p className="text-sm text-slate-500">Booking Type: CURRENT · Amount: ₹{result.amount}</p>
            <div className="flex gap-2 justify-center">
              <Button data-testid="current-issue-btn" onClick={() => navigate(`/protocol/issue?txn=${result.transaction_id}`)}
                className="bg-saffron-500 hover:bg-saffron-600 text-white">Capture Photo & Issue Ticket</Button>
              <Button variant="outline" data-testid="current-new-btn" onClick={() => { setResult(null); setForm({ devotee_name: "", aadhaar: "", mobile: "", reference_name: "", num_devotees: 1, darshan_type_id: "", paid_free: "", payment_details: "" }); }}>
                New Booking
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="current-booking-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-1">Current Booking</h1>
      <p className="text-sm text-slate-500 mb-5">Direct counter registration — transaction is created immediately and is ready for ticket issue.</p>
      <form onSubmit={submit}>
        <Card className="card-gold-top shadow-sm">
          <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Devotee Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Devotee Name *</Label><Input data-testid="current-devotee-name" value={form.devotee_name} onChange={(e) => set("devotee_name", e.target.value)} required /></div>
            <div><Label>Aadhaar Number</Label><Input data-testid="current-aadhaar" value={form.aadhaar} onChange={(e) => set("aadhaar", e.target.value)} pattern="[0-9]{12}" title="12-digit Aadhaar" /></div>
            <div><Label>Mobile Number *</Label><Input data-testid="current-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} pattern="[0-9]{10}" required /></div>
            <div><Label>Reference Name</Label><Input data-testid="current-reference" value={form.reference_name} onChange={(e) => set("reference_name", e.target.value)} /></div>
            <div><Label>Number of Devotees *</Label><Input data-testid="current-num-devotees" type="number" min={1} max={20} value={form.num_devotees} onChange={(e) => set("num_devotees", e.target.value)} required /></div>
            <div>
              <Label>Darshan Type *</Label>
              <Select value={form.darshan_type_id} onValueChange={(v) => set("darshan_type_id", v)}>
                <SelectTrigger data-testid="current-darshan-select"><SelectValue placeholder="Select Darshan Type" /></SelectTrigger>
                <SelectContent>{meta.darshan_types.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} {d.amount > 0 ? `(₹${d.amount})` : "(Free)"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Paid Devotees</Label>
              <Input data-testid="current-paid-devotees" type="number" min={0} max={totalN} disabled={isFreeType}
                value={paidN} onChange={(e) => set("paid_devotees", Math.max(0, Math.min(Number(e.target.value) || 0, totalN)))} />
            </div>
            <div>
              <Label>Free Devotees (auto)</Label>
              <Input data-testid="current-free-devotees" value={freeN} disabled className="bg-gold-50" />
            </div>
            <div>
              <Label>Payment Details</Label>
              <Select value={form.payment_details} onValueChange={(v) => set("payment_details", v === "NONE" ? "" : v)}>
                <SelectTrigger data-testid="current-payment"><SelectValue placeholder="Select payment mode" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">Not Applicable / Free</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Card">Card</SelectItem>
                  <SelectItem value="DD / Cheque">DD / Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 flex items-center justify-between bg-gold-50 border border-gold-300 rounded-md px-4 py-3">
              <span className="text-xs font-bold uppercase tracking-wider text-gold-700">
                Total Amount (auto) · {selectedDt ? `₹${selectedDt.amount} × ${paidN} paid · ${freeN} free · total ${totalN}` : "Select darshan type"}
              </span>
              <span className="font-heading text-2xl font-bold text-maroon-900" data-testid="current-total-amount">₹{totalAmount.toLocaleString("en-IN")}</span>
            </div>
          </CardContent>
        </Card>
        <Button type="submit" disabled={loading} data-testid="current-submit-btn"
          className="w-full h-12 mt-5 bg-maroon-700 hover:bg-maroon-800 text-gold-100 font-heading tracking-widest text-base">
          {loading ? "REGISTERING…" : "REGISTER & GENERATE TRANSACTION ID"}
        </Button>
      </form>
    </div>
  );
}
